import type { ContactInput, FormInput, FormSubmitPayload, LeadInput } from '@jlg-website/shared';
import { CONTACT_FIELD_KEYS } from '@jlg-website/shared';
import { collectQuestionAnswers, toLeadAnswers } from './marketing';
import { compute as computeScore } from './score';
import { Plugins } from '@jlg-website/shared';

/**
 * Persists the lead through the `/api/v1/lead` endpoint — the same route the contact form
 * (`useContact`) posts to — instead of a Server Action, which the middleware would reject
 * with 405 when POSTed to a public page route. The `LeadInput` is assembled on the client:
 * campaign/lead-type/offer come from the marketing bootstrap and the answers/contact are
 * split out of the submit payload locally (mirroring the server's old `collectAnswers`).
 */

export type SubmitResult = {
  is_qualified: boolean;
  is_duplicate: boolean;
  lead_id: number;
  answers: LeadInput['lead']['answers'];
  contact: ContactInput;
};

/** Pulls the contact object out of the submit payload's contact block. */
function collectContact(payload: FormSubmitPayload): ContactInput | null {
  const contact: Record<string, string | boolean> = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    zip_code: '',
    details: '',
    consent: true,
  };

  const contactFieldKeys = CONTACT_FIELD_KEYS as readonly string[];
  let hasContactField = false;

  for (const [key, answer] of Object.entries(payload.answers)) {
    if (contactFieldKeys.includes(key)) {
      hasContactField = true;
      contact[key] = `${answer.value}`;
    }
  }

  return hasContactField ? (contact as ContactInput) : null;
}

function buildLeadInput(
  schema: FormInput,
  payload: FormSubmitPayload,
  contact: ContactInput,
  answers: LeadInput['lead']['answers'],
  plugins: Plugins
): LeadInput {
  const session = typeof window !== 'undefined' ? window.JLG?.analytics?.session : undefined;
  const marketing = typeof window !== 'undefined' ? window.JLG?.marketing : undefined;

  return {
    session: session?.session_id ?? '',
    form: { id: schema.id ?? 0 },
    campaign: {
      id: marketing?.campaign?.id ?? 0,
      // `leads.lead_type_id` is a FK to `claim_sections.id`: use the lead type's internal id,
      // not its external marketing id (mirrors useContact).
      lead_type: {
        id: marketing?.lead_type?.internal_id ?? 0,
        lead_type_name: window.JLG?.marketing?.lead_type?.name ?? '',
      },
      lead_source: { id: marketing?.lead_source?.id ?? 0 },
      lead_score_baseline: window.JLG?.marketing?.lead_score_baseline ?? 0,
      offer: { id: marketing?.offer?.id ?? 0 },
    },
    lead: {
      answers,
      contact,
      is_qualified: payload.is_qualified,
      is_duplicate: false,
      score: computeScore(answers),
    },
    is_2fa_enabled: false,
    is_schedule_callback_enabled: false,
    analytics: session
      ? session
      : {
          ip_address: null,
          user_agent: null,
          url: typeof window !== 'undefined' ? window.location.href : null,
        },
    plugins,
  };
}

export async function submitLead(
  schema: FormInput,
  payload: FormSubmitPayload,
  plugins: Plugins
): Promise<SubmitResult> {
  const contact = collectContact(payload);
  if (!contact) throw new Error('Contact information is missing');

  const answers = toLeadAnswers(collectQuestionAnswers(payload));
  const leadInput = buildLeadInput(schema, payload, contact, answers, plugins);

  const response = await fetch('/api/v1/lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(leadInput),
  });
  if (!response.ok) throw new Error('Unable to create lead');

  const lead = await response.json();

  return {
    is_qualified: payload.is_qualified,
    // Duplicate detection runs server-side; it comes back on the stored session.
    is_duplicate: lead?.session?.lead?.is_duplicate === true,
    lead_id: lead?.id,
    answers,
    contact,
  };
}
